import React, { useCallback } from 'react';
import {
  ColumnBlockWrapper,
  ColumnBlockCol,
  EditBlockCover,
  EditBlockButton,
  RelativeWrapper,
} from './atoms/Atoms';
import { Block, emptyBlockContent } from '../../../../../config/api/models/Block';
import { WysiwygEditor } from '../../../../shared/components';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/useAppDispatch';
import { setHoverBlock, setNextBlock } from '../../../../shared/slices/blocks/entity/BlocksSlice';
import { patchBlock, toggleBlockIsPrintable } from '../../../../shared/slices/blocks/api/blocks';

import { useClickOutside } from '../Hooks/useClickOutside';
import FroalaEditor from 'react-froala-wysiwyg';
import LabeledCheckbox from 'src/modules/shared/components/LabeledCheckbox/LabeledCheckbox';
import { useUser } from 'src/modules/shared';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useTheme } from '@material-ui/core';

export type ColumnBlockProps = {
  block: Block;
  pageId: string;
  bookId: string;
  hoverActive?: boolean;
  readOnly?: boolean;
  onSelectBlock: Function;
};

let editorRefs: Record<string, React.MutableRefObject<FroalaEditor | null>> = {};

export const ColumnBlock: React.FC<ColumnBlockProps> = ({
  block,
  readOnly,
  bookId,
  pageId,
  onSelectBlock,
}) => {
  const [selectedBlock, setSelectedBlock] = React.useState<string | undefined>(undefined);
  const [hoverColumn, setHoverColumn] = React.useState<string | undefined>(undefined);
  const nextBlock = useAppSelector((state) => state.blocks.nextBlock);

  const [isEditorReadOnly] = React.useState(selectedBlock !== undefined);
  const dispatch = useAppDispatch();
  const { isAuthor } = useUser();
  const theme = useTheme();

  const handleMouseEnter = React.useCallback(
    (blockId: string) => {
      setHoverColumn(blockId);
      dispatch(setNextBlock(blockId));
    },
    [setHoverColumn, dispatch]
  );
  const handleMouseLeave = React.useCallback(() => {
    setHoverColumn('');
    dispatch(setNextBlock(undefined));
  }, [setHoverColumn, dispatch]);

  const { wrapperRef, handleClickOutside } = useClickOutside();

  const requestPatchBlock = useCallback(() => {
    if (editorRefs[selectedBlock!]?.current?.getEditor().el.innerHTML !== undefined) {
      dispatch(
        patchBlock({
          bookId,
          pageId,
          blockId: block.id as string,
          payload: {
            columns: block.columns?.map((column) => {
              return {
                ...column,
                content:
                  editorRefs[column.id!]?.current?.getEditor().el.innerHTML ?? column.content,
                width: column.client_settings.width,
              };
            }),
            type: 'column',
          },
        })
      );
    }
  }, [block, pageId, bookId, selectedBlock, dispatch]);

  React.useEffect(() => {
    const triggerAutosave = (event: MouseEvent) => {
      function closeEditMode() {
        requestPatchBlock();
        setSelectedBlock(nextBlock ?? undefined);
        dispatch(setHoverBlock(undefined));
        onSelectBlock(nextBlock ?? undefined);
        editorRefs = {};
      }
      if (nextBlock !== selectedBlock) {
        return closeEditMode();
      }
      const wiris = document.getElementById('wrs_modal_dialogContainer[0]');
      const wirisChildren: Node[] | null =
        wiris && Array.from(wiris?.childNodes).map((child) => child);
      return handleClickOutside(event, closeEditMode, [
        wrapperRef.current,
        wiris,
        wirisChildren?.map((child) => child),
      ]);
    };
    if (selectedBlock) {
      window.addEventListener('mousedown', triggerAutosave);
    }
    return () => {
      window.removeEventListener('mousedown', triggerAutosave);
    };
    //eslint-disable-next-line
  }, [wrapperRef, handleClickOutside, editorRefs]);

  const handleEditorRefs = React.useCallback((editor, columnId) => {
    if (editor?.current) {
      editorRefs[columnId] = editor;
    }
  }, []);

  const handlePrintableChange = useCallback(() => {
    dispatch(
      toggleBlockIsPrintable({
        bookId,
        pageId,
        blockId: block.id,
      })
    );
  }, [dispatch, block.id, bookId, pageId]);

  return (
    <>
      <ColumnBlockWrapper ref={wrapperRef as any}>
        {block?.columns?.map((columnBlock) => (
          <ColumnBlockCol
            onMouseEnter={() => !readOnly && handleMouseEnter(columnBlock.id)}
            onMouseLeave={handleMouseLeave}
            width={columnBlock.client_settings?.width}
            key={columnBlock.id}
          >
            {hoverColumn === columnBlock.id && columnBlock.id !== selectedBlock && (
              <EditBlockCover onClick={() => setSelectedBlock(columnBlock.id)}>
                <EditBlockButton
                  faIcon={SpriteIconsLight.PENCIL as any}
                  large={true}
                  dashed={true}
                  bgColor={theme.palette.background.default}
                  color={theme.palette.secondary.main}
                />
              </EditBlockCover>
            )}
            <RelativeWrapper
              className={
                !columnBlock.content || columnBlock.content === emptyBlockContent
                  ? 'is-empty'
                  : undefined
              }
            >
              <WysiwygEditor
                id={`wysiwyg_${columnBlock.id}`}
                value={(columnBlock.content as string) || ''}
                readOnly={!isEditorReadOnly && selectedBlock !== columnBlock.id}
                forceTitleOnTop={false}
                onLoadRef={(editor) => handleEditorRefs(editor, columnBlock.id)}
                className={columnBlock.is_printable ? undefined : 'hide-on-print'}
              />
            </RelativeWrapper>
          </ColumnBlockCol>
        ))}
      </ColumnBlockWrapper>
      {!readOnly && isAuthor && block.is_printable !== undefined && (
        <LabeledCheckbox
          label="Opnemen in print"
          tooltipMessage="Vink uit als deze blok niet mee mag in print"
          checked={block.is_printable}
          hideOnPrint={handlePrintableChange}
        />
      )}
    </>
  );
};

export default ColumnBlock;
